import { Controller } from "@hotwired/stimulus";
import "./component.scss";

const MOBILE_QUERY = "(max-width: 47.5em)";

export default class extends Controller {
  static targets = [
    "popup", "picker", "editor", "editorTitle", "search", "empty", "count", "connected",
    "configuredButton", "connectedItem", "availableButton", "platformInput", "valueInput", "prefix",
    "remove", "error",
  ];

  static values = {
    errors: Object,
    links: { type: Object, default: {} },
    hiddenPlatforms: { type: Array, default: [] },
  };

  openPicker() {
    this.reset();
    this.showPopup();
  }

  choose(event) {
    this.showPopup();
    const { platform, label, prefix, storedPrefix, placeholder } = event.currentTarget.dataset;
    this.platformInputTarget.value = platform;
    this.valueInputTarget.value = this.editableValue(platform, storedPrefix);
    this.valueInputTarget.placeholder = placeholder;
    this.valueInputTarget.inputMode = platform === "whatsapp" ? "numeric" : platform === "email" ? "email" : "url";
    this.clearError();
    this.prefixTarget.textContent = prefix;
    this.editorTitleTarget.textContent = label;
    this.removeTarget.hidden = !this.linksValue[platform];

    this.editorTarget.querySelectorAll("[data-platform-icon]").forEach((icon) => {
      icon.hidden = icon.dataset.platformIcon !== platform;
    });
    this.pickerTarget.hidden = true;
    this.editorTarget.hidden = false;
    this.focusAfterTransition(this.valueInputTarget);
  }

  save(event) {
    event?.preventDefault();
    const platform = this.platformInputTarget.value;
    const value = this.valueInputTarget.value.trim();
    if (!platform || !this.validate()) return;
    const source = this.findPlatformControl(platform);
    const storedPrefix = source?.dataset.storedPrefix || "";
    this.linksValue = { ...this.linksValue, [platform]: this.normalizedValue(platform, value, storedPrefix) };
    this.hiddenPlatformsValue = this.hiddenPlatformsValue.filter((candidate) => candidate !== platform);
    this.syncPlatforms();
    this.notifyChange();
    this.back();
  }

  removeConnected(event) {
    const platform = event.currentTarget?.dataset?.platform;
    this.removePlatform(platform);
    this.focusAfterTransition(this.searchTarget);
  }

  remove() {
    const platform = this.platformInputTarget.value;
    this.removePlatform(platform, { close: true });
  }

  removePlatform(platform, { close = false } = {}) {
    if (!platform) return;
    const nextLinks = { ...this.linksValue };
    if (!nextLinks[platform]) return;
    delete nextLinks[platform];
    this.linksValue = nextLinks;
    this.hiddenPlatformsValue = this.hiddenPlatformsValue.filter((candidate) => candidate !== platform);
    if (this.platformInputTarget.value === platform) {
      if (close) this.closePopup();
      else this.back();
    }
    this.syncPlatforms();
    this.notifyChange();
  }

  back() {
    this.editorTarget.hidden = true;
    this.pickerTarget.hidden = false;
    this.focusAfterTransition(this.searchTarget);
  }

  focusAfterTransition(desktopTarget) {
    requestAnimationFrame(() => {
      if (!matchMedia(MOBILE_QUERY).matches) {
        desktopTarget.focus({ preventScroll: true });
        return;
      }

      this.popupTarget.setAttribute("tabindex", "-1");
      this.popupTarget.focus({ preventScroll: true });
    });
  }

  filter() {
    const query = this.searchTarget.value.trim().toLocaleLowerCase();
    let visibleCount = 0;
    this.connectedItemTargets.forEach((item) => {
      const configured = Boolean(this.linksValue[item.dataset.platform]);
      const visible = configured && item.dataset.searchLabel.includes(query);
      item.hidden = !visible;
      if (visible) visibleCount += 1;
    });
    this.availableButtonTargets.forEach((button) => {
      const configured = Boolean(this.linksValue[button.dataset.platform]);
      const visible = !configured && button.dataset.searchLabel.includes(query);
      button.hidden = !visible;
      if (visible) visibleCount += 1;
    });
    this.emptyTarget.hidden = visibleCount > 0;
  }

  reset() {
    this.editorTarget.hidden = true;
    this.pickerTarget.hidden = false;
    this.searchTarget.value = "";
    this.filter();
  }

  closePopup() {
    this.popupTarget.close();
  }

  popupClosed() {
    this.popupTarget.removeAttribute("tabindex");
    this.reset();
  }

  showPopup() {
    if (!this.popupTarget.open) this.popupTarget.showModal();
  }

  replaceState(links, hiddenPlatforms) {
    this.linksValue = { ...links };
    this.hiddenPlatformsValue = [...hiddenPlatforms];
    this.syncPlatforms();
    const platform = this.platformInputTarget.value;
    if (platform && this.editorTarget.hidden) {
      const source = this.findPlatformControl(platform);
      this.valueInputTarget.value = this.editableValue(platform, source?.dataset.storedPrefix || "");
      this.removeTarget.hidden = !this.linksValue[platform];
    }
  }

  syncPlatforms() {
    const configured = Object.keys(this.linksValue).filter((platform) => this.linksValue[platform]);
    this.configuredButtonTargets.forEach((button) => {
      const active = configured.includes(button.dataset.platform);
      button.hidden = !active;
      button.classList.toggle("is-hidden", active && this.hiddenPlatformsValue.includes(button.dataset.platform));
    });
    this.connectedItemTargets.forEach((item) => { item.hidden = !configured.includes(item.dataset.platform); });
    this.availableButtonTargets.forEach((button) => { button.hidden = configured.includes(button.dataset.platform); });
    this.connectedTarget.hidden = configured.length === 0;
    this.countTarget.textContent = this.countTarget.textContent.replace(/^\d+/, configured.length.toString());
    this.filter();
  }

  findPlatformControl(platform) {
    return [...this.configuredButtonTargets, ...this.availableButtonTargets]
      .find((button) => button.dataset.platform === platform);
  }

  editableValue(platform, storedPrefix) {
    const value = (this.linksValue[platform] || "").toString();
    return storedPrefix && value.startsWith(storedPrefix) ? value.slice(storedPrefix.length) : value;
  }

  clearError() {
    this.valueInputTarget.setCustomValidity("");
    this.valueInputTarget.setAttribute("aria-invalid", "false");
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  validate() {
    this.clearError();
    const platform = this.platformInputTarget.value;
    const value = this.valueInputTarget.value.trim();
    const source = this.findPlatformControl(platform);
    try {
      if (!value) throw new Error("required");
      this.normalizedValue(platform, value, source?.dataset.storedPrefix || "");
      return true;
    } catch (error) {
      const message = this.errorsValue[error.message] || this.errorsValue.url;
      this.valueInputTarget.setCustomValidity(message);
      this.valueInputTarget.setAttribute("aria-invalid", "true");
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
      return false;
    }
  }

  normalizedValue(platform, value, storedPrefix) {
    if (platform === "email") {
      const email = value.replace(/^mailto:/i, "");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("email");
      return email;
    }
    if (platform === "whatsapp") {
      if (!/^[0-9]+$/.test(value)) throw new Error("whatsapp");
      return `${storedPrefix}${value}`;
    }
    if (platform === "generic_link") return this.webUrl(value).href;
    if (platform === "spotify") {
      const url = this.webUrl(value);
      if (url.hostname !== "open.spotify.com" || url.pathname === "/") throw new Error("platform");
      return url.href;
    }
    const expectedHost = new URL(storedPrefix).hostname;
    const bareUrl = value.replace(/^www\./i, "").toLowerCase().startsWith(`${expectedHost}/`);
    if (/^https?:\/\//i.test(value) || bareUrl) {
      const url = this.webUrl(bareUrl ? `https://${value}` : value);
      if (url.hostname.replace(/^www\./, "") !== expectedHost || url.pathname === "/") throw new Error("platform");
      return url.href;
    }
    const username = value.replace(/^@/, "");
    if (!/^[\p{L}\p{N}_.-]+$/u.test(username)) throw new Error("username");
    return new URL(`${storedPrefix}${username}`).href;
  }

  webUrl(value) {
    let url;
    try { url = new URL(value); } catch (_error) { throw new Error("url"); }
    if (url.protocol !== "https:" || !url.hostname || url.username || url.password) throw new Error("url");
    return url;
  }

  notifyChange() {
    this.dispatch("change", {
      detail: { links: this.linksValue, hiddenPlatforms: this.hiddenPlatformsValue },
    });
  }
}
